import React, { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Select, Input, Modal, notification } from "antd";
import {
  Upload,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  FileText,
  BookOpen,
  List,
  X,
} from "lucide-react";
import DefaultLayout from "../layouts/default/Default.layout";
import MarkdownEditor from "../components/MarkdownEditor";
import usePageTitle from "../../core/hooks/UsePageTitle";
import useBancas from "../../core/hooks/UseBanca";
import useMaterias from "../../core/hooks/UseMaterias";
import QuestaoService from "../../sdk/services/QuestaoService";
import SharedTextService from "../../sdk/services/SharedTextService";
import QuestionSourceService from "../../sdk/services/QuestionSourceService";
import FileService from "../../sdk/services/FileService";
import SharedTextEditorModal from "../components/SharedTextEditor/SharedTextEditorModal";
import { Acertei, SharedText } from "../../sdk/@types/Acertei";
import { TagDto } from "../../sdk/@types/TagDto";
import { readApiError } from "../../sdk/errors/apiError";
import { FOCUS_RING, cx } from "../components/filters/tokens";

const { TextArea } = Input;
const { Option } = Select;

// ─── Types ───────────────────────────────────────────────────────────────────

type Dificuldade = "Fácil" | "Média" | "Difícil";
type StepId = "upload" | "review";

interface ImportChoice {
  description: string;
  correct: boolean;
  rationale: string;
}

interface ImportSharedText {
  tempId: string;
  title: string;
  source: string;
  body: string;
}

export interface ImportQuestion {
  id: string;
  testingAgencyId: string;
  bancaHint: string;
  /** Ano declarado por BANCA_ANO, ou extraído do próprio BANCA ("UEG 2018"). */
  bancaAnoHint: string;
  subjectId: string;
  materiaHint: string;
  difficulty: Dificuldade;
  keyword: string[];
  statement: string;
  choices: ImportChoice[];
  /** Texto base declarado no próprio arquivo (resolvido para um id real no envio). */
  sharedTextTempId?: string;
  /** Texto base que já existe no banco, informado por TEXTO_BASE_ID. */
  sharedTextRefId?: string;
  sharedTextOrder?: number;
  status: "ok" | "error" | "pending";
  errors: string[];
  /**
   * Mensagem da API na última tentativa de envio desta questão.
   *
   * Separada de `errors` porque `recompute` reescreve `errors` a cada edição, e
   * porque a origem é outra: `errors` é o que esta tela sabe validar, isto é o
   * que o backend recusou. Fica visível até o próximo envio.
   */
  serverError?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// A alternativa E é opcional: o slot sempre aparece no editor, mas só é salva se preenchida.
const LETTERS = ["A", "B", "C", "D", "E"] as const;

// Sentinela para "+ Nova prova…" no <select> do cabeçalho da revisão.
const NOVA_PROVA = "__new__";

const DIFF_MAP: Record<string, Dificuldade> = {
  "fácil": "Fácil", "facil": "Fácil",
  "médio": "Média", "medio": "Média",
  "difícil": "Difícil", "dificil": "Difícil",
};

const DIFF_LABELS: Record<Dificuldade, string> = {
  "Fácil": "Fácil",
  "Média": "Média",
  "Difícil": "Difícil",
};

const TAG_MAX_LENGTH = 40;

// ─── Banca e matéria: da dica do arquivo para o id real ───────────────────────

/**
 * As dicas do arquivo (`BANCA:`, `MATERIA:`) são nomes; a API quer ObjectIDs.
 *
 * Antes nada as resolvia: `parseTxt` guardava o texto só para exibi-lo como
 * "(arquivo: UEG)" ao lado dos dois comboboxes, que nasciam vazios em TODAS as
 * questões. Num lote de 65 isso são 130 seleções manuais — e o que não fosse
 * selecionado tomava 400 no envio.
 */

/** Sem acentos, minúsculo, espaços colapsados — casa "Física" com "FISICA ". */
function normalizarNome(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * `TestingAgency` é `{year, name}` e o combobox mostra "2018 - UEG", então o
 * nome sozinho é ambíguo quando a mesma banca existe em vários anos. O ano pode
 * vir numa chave própria (`BANCA_ANO:`) ou grudado no nome — "UEG 2018",
 * "2018 - UEG", "2018/UEG" —, e esta função aceita as duas formas.
 */
function separarBancaHint(hint: string): { nome: string; ano?: string } {
  const ano = hint.match(/\b(?:19|20)\d{2}\b/)?.[0];
  const nome = hint
    .replace(/\b(?:19|20)\d{2}\b/, "")
    .replace(/[-/|·,]/g, " ")
    .trim();
  return { nome, ano };
}

/**
 * `""` sempre que o **ano** não vier do arquivo — nunca infere.
 *
 * Uma versão anterior casava só pelo nome quando havia um único cadastro com
 * ele. Parece inofensivo e não é: no cadastro deste ambiente existe apenas
 * "2026 - UEG", e um arquivo da prova **de 2018** saía com as 65 questões
 * carimbadas como UEG 2026 — dado errado, gravado em silêncio, e muito mais
 * caro de descobrir depois do que um combobox vazio. O ano é parte da
 * identidade da banca (`TestingAgency` é `{year, name}`), então sem ele
 * declarado não há casamento; o seletor de lote resolve as 65 num clique.
 */
function resolverBanca(hint: string, anoHint: string, bancas: any[]): string {
  const alvo = separarBancaHint(hint);
  const ano = anoHint.trim() || alvo.ano;
  const nome = normalizarNome(alvo.nome);
  if (!nome || !ano) return "";

  const comAno = bancas.filter(
    (b) =>
      normalizarNome(b?.name ?? "") === nome && String(b?.year ?? "").trim() === ano,
  );
  return comAno.length === 1 ? String(comAno[0].id) : "";
}

function resolverMateria(hint: string, materias: any[]): string {
  const nome = normalizarNome(hint);
  if (!nome) return "";
  const achadas = materias.filter((m) => normalizarNome(m?.name ?? "") === nome);
  return achadas.length === 1 ? String(achadas[0].id) : "";
}

/**
 * Preenche as bancas/matérias **vazias** a partir das dicas do arquivo.
 *
 * Só escreve em campo vazio, então nunca desfaz uma escolha do revisor, e
 * devolve o MESMO array quando não há nada a preencher — é isso que deixa o
 * efeito que a chama rodar a cada edição sem laço nem re-render.
 */
export function aplicarDicas(
  qs: ImportQuestion[],
  bancas: any[],
  materias: any[],
): ImportQuestion[] {
  let mudou = false;
  const next = qs.map((q) => {
    const patch: Partial<ImportQuestion> = {};
    if (!q.testingAgencyId) {
      const id = resolverBanca(q.bancaHint, q.bancaAnoHint, bancas);
      if (id) patch.testingAgencyId = id;
    }
    if (!q.subjectId) {
      const id = resolverMateria(q.materiaHint, materias);
      if (id) patch.subjectId = id;
    }
    if (Object.keys(patch).length === 0) return q;
    mudou = true;
    return recompute({ ...q, ...patch });
  });
  return mudou ? next : qs;
}

// ─── Validation ───────────────────────────────────────────────────────────────

export function validateQ(q: ImportQuestion): string[] {
  const errs: string[] = [];
  // Banca e matéria são erro, não detalhe: `Question` no Go marca as duas com
  // `binding:"required,len=24"` (cmd/internal/database/questions.go:31-32),
  // então o POST /questions nem chega ao handler sem elas — devolve 400 no
  // bind. Sem estas duas linhas a questão exibia "OK", entrava na contagem do
  // modal de confirmação e só então falhava, em silêncio, no envio.
  if (!q.testingAgencyId) errs.push("Banca não selecionada");
  if (!q.subjectId) errs.push("Matéria não selecionada");
  if (!q.statement.trim()) errs.push("Enunciado vazio");
  if (!q.choices[0]?.description.trim()) errs.push("Alternativa A vazia");
  if (!q.choices[1]?.description.trim()) errs.push("Alternativa B vazia");
  const cc = q.choices.filter((c) => c.correct).length;
  if (cc === 0) errs.push("Nenhuma alternativa correta marcada");
  if (cc > 1) errs.push("Mais de uma alternativa marcada como correta");
  return errs;
}

function recompute(q: ImportQuestion): ImportQuestion {
  const errors = validateQ(q);
  return { ...q, errors, status: errors.length === 0 ? "ok" : "error" };
}

// ─── TXT Parser ──────────────────────────────────────────────────────────────

const KNOWN_KEYS = [
  // BANCA_ANO vem antes de BANCA pelo mesmo motivo que TEXTO_BASE_ID vem antes
  // de TEXTO_BASE: o casamento é por prefixo e o primeiro da lista ganha.
  "ENUNCIADO", "BANCA_ANO", "BANCA", "MATERIA",
  "A", "B", "C", "D", "E",
  "GABARITO", "DIFICULDADE", "TAGS", "COMENTARIO",
  // Texto base compartilhado
  "TEXTO_BASE_TITULO", "TEXTO_BASE_FONTE", "TEXTO_BASE_ID", "TEXTO_BASE", "ORDEM",
];

/**
 * Sentinela que fecha o corpo do texto base.
 *
 * É obrigatória, não açúcar: o corpo é consumido CRU até esta linha porque uma
 * passagem em forma de diálogo tem linhas como "A: Bom dia" — que o parser de
 * chaves engoliria como a alternativa A. Sem a sentinela, todo texto dialogado
 * seria destruído silenciosamente na importação.
 */
const TEXT_END = "FIM_TEXTO_BASE";

export function parseTxt(raw: string): { texts: ImportSharedText[]; questions: ImportQuestion[] } {
  // Split by --- separator; filter blocks that have no meaningful content
  const blocks = raw
    .split(/^-{3,}\s*$/m)
    .filter((b) =>
      b.split("\n").some((l) => l.trim() && !l.trim().startsWith("#"))
    );

  const texts: ImportSharedText[] = [];
  const questions: ImportQuestion[] = [];

  // Texto declarado pelo último bloco de declaração; vale para os blocos
  // seguintes até um bloco com apenas FIM_TEXTO_BASE limpar o vínculo.
  let textoCorrente: string | null = null;

  /**
   * Contador de ORDEM por grupo, e não um contador único do arquivo.
   *
   * A chave é o tempId (texto declarado aqui) ou o TEXTO_BASE_ID (texto já
   * cadastrado). Com um contador único, dois TEXTO_BASE_ID seguidos saíam como
   * 1,2 e 3,4 — o segundo texto começava onde o primeiro parou.
   *
   * Um ORDEM explícito também empurra o contador, para que uma questão sem
   * ORDEM depois de "ORDEM: 2" receba 3 em vez de repetir 2.
   */
  const ordemPorGrupo = new Map<string, number>();
  const proximaOrdem = (grupo: string, informada: number) => {
    const atual = ordemPorGrupo.get(grupo) ?? 0;
    const ordem = Number.isFinite(informada) ? informada : atual + 1;
    ordemPorGrupo.set(grupo, Math.max(atual, ordem));
    return ordem;
  };

  blocks.forEach((block, idx) => {
    const fields: Record<string, string> = {};
    let currentKey: string | null = null;
    let currentLines: string[] = [];
    let dentroDoTexto = false;
    let limpaVinculo = false;

    const flush = () => {
      if (currentKey !== null) {
        fields[currentKey] = currentLines.join("\n").trimEnd();
      }
    };

    for (const line of block.split("\n")) {
      const trimmed = line.trim();

      if (dentroDoTexto) {
        if (trimmed.toUpperCase() === TEXT_END) {
          flush();
          currentKey = null;
          currentLines = [];
          dentroDoTexto = false;
        } else {
          currentLines.push(line); // cru, inclusive linhas em branco e "A:"
        }
        continue;
      }

      if (trimmed.startsWith("#")) continue;

      if (trimmed.toUpperCase() === TEXT_END) {
        // FIM_TEXTO_BASE solto encerra o grupo aberto.
        limpaVinculo = true;
        continue;
      }

      const upperTrimmed = trimmed.toUpperCase();
      const matched = KNOWN_KEYS.find((k) => upperTrimmed.startsWith(k + ":"));

      if (matched) {
        flush();
        currentKey = matched;
        const resto = trimmed.slice(matched.length + 1).trim();
        if (matched === "TEXTO_BASE") {
          dentroDoTexto = true;
          currentLines = resto ? [resto] : [];
        } else {
          currentLines = [resto];
        }
      } else if (currentKey === "ENUNCIADO") {
        // Preserve raw line including blank lines and spacing
        currentLines.push(line);
      } else if (currentKey !== null && trimmed) {
        currentLines.push(trimmed);
      }
    }
    flush();

    const get = (key: string) => fields[key.toUpperCase()] ?? "";

    // ── Bloco de declaração de texto base (sem ENUNCIADO) ──
    if (get("TEXTO_BASE").trim() && !get("ENUNCIADO").trim()) {
      const tempId = `t-${texts.length}`;
      texts.push({
        tempId,
        title: get("TEXTO_BASE_TITULO") || `Texto ${texts.length + 1}`,
        source: get("TEXTO_BASE_FONTE"),
        body: get("TEXTO_BASE"),
      });
      textoCorrente = tempId;
      return;
    }

    if (limpaVinculo && !get("ENUNCIADO").trim()) {
      textoCorrente = null;
      return;
    }

    const ans = get("GABARITO").toUpperCase();
    const diff = get("DIFICULDADE").toLowerCase();
    const tagsRaw = get("TAGS");

    // TEXTO_BASE_ID aponta para um texto que já existe no banco e tem
    // precedência sobre o texto declarado no arquivo.
    const refId = get("TEXTO_BASE_ID").trim();
    // Chave do grupo para a numeração: o id real quando existe, senão o texto
    // declarado no arquivo. `null` = questão avulsa, sem ORDEM.
    const grupo = refId || textoCorrente;
    const ordemInformada = parseInt(get("ORDEM"), 10);

    const q: ImportQuestion = {
      id: `q-${idx}`,
      testingAgencyId: "",
      bancaHint: get("BANCA"),
      bancaAnoHint: get("BANCA_ANO"),
      subjectId: "",
      materiaHint: get("MATERIA"),
      difficulty: DIFF_MAP[diff] ?? "Média",
      keyword: tagsRaw
        ? tagsRaw.split(",").map((t) => t.trim()).filter(Boolean)
        : [],
      statement: get("ENUNCIADO"),
      choices: LETTERS.map((letter) => ({
        description: get(letter),
        correct: ans === letter,
        rationale: ans === letter ? get("COMENTARIO") : "",
      })),
      sharedTextTempId: refId ? undefined : textoCorrente ?? undefined,
      sharedTextRefId: refId || undefined,
      sharedTextOrder: grupo ? proximaOrdem(grupo, ordemInformada) : undefined,
      status: "pending",
      errors: [],
    };

    if (limpaVinculo) {
      textoCorrente = null;
    }

    questions.push(recompute(q));
  });

  return { texts, questions };
}

// ─── Demo data ────────────────────────────────────────────────────────────────

const DEMO: ImportQuestion[] = [
  recompute({
    id: "q-0",
    testingAgencyId: "",
    bancaHint: "CESPE",
    bancaAnoHint: "",
    subjectId: "",
    materiaHint: "Redes de Computadores",
    difficulty: "Média",
    keyword: ["Redes", "Segurança"],
    statement:
      "Qual a principal função do protocolo HTTPS na camada de aplicação do modelo OSI, considerando a utilização de certificados digitais para garantir a autenticidade e o sigilo das informações trafegadas?",
    choices: [
      { description: "Prover comunicação criptografada e identificação segura de um servidor web.", correct: true, rationale: "O HTTPS usa TLS/SSL para criptografar o canal e certificados digitais para autenticar o servidor." },
      { description: "Realizar o roteamento de pacotes entre redes distintas de forma anônima.", correct: false, rationale: "" },
      { description: "Garantir que a banda de rede seja otimizada para streaming de vídeo.", correct: false, rationale: "" },
      { description: "Estabelecer conexões UDP para transmissão de dados em tempo real.", correct: false, rationale: "" },
      { description: "Comprimir o conteúdo das páginas antes do envio ao navegador.", correct: false, rationale: "" },
    ],
    status: "pending",
    errors: [],
  }),
  recompute({
    id: "q-1",
    testingAgencyId: "",
    bancaHint: "FCC",
    bancaAnoHint: "",
    subjectId: "",
    materiaHint: "Sistemas Operacionais",
    difficulty: "Média",
    keyword: ["OSI", "Redes"],
    statement: "Considerando o modelo OSI, em qual camada reside o protocolo TCP?",
    choices: [
      { description: "Camada de Aplicação", correct: false, rationale: "" },
      { description: "Camada de Transporte", correct: true, rationale: "O TCP opera na camada de Transporte (camada 4) do modelo OSI." },
      { description: "Camada de Rede", correct: false, rationale: "" },
      { description: "Camada de Enlace", correct: false, rationale: "" },
    ],
    status: "pending",
    errors: [],
  }),
  recompute({
    id: "q-2",
    testingAgencyId: "",
    bancaHint: "FGV",
    bancaAnoHint: "",
    subjectId: "",
    materiaHint: "Arquitetura de Computadores",
    difficulty: "Fácil",
    keyword: [],
    statement:
      "A arquitetura Von Neumann é caracterizada por possuir memória compartilhada para dados e instruções.",
    choices: [
      { description: "Verdadeiro", correct: true, rationale: "" },
      { description: "Falso", correct: false, rationale: "" },
      { description: "Depende da implementação", correct: false, rationale: "" },
      { description: "Apenas para instruções", correct: false, rationale: "" },
    ],
    status: "pending",
    errors: [],
  }),
  recompute({
    id: "q-3",
    testingAgencyId: "",
    bancaHint: "VUNESP",
    bancaAnoHint: "",
    subjectId: "",
    materiaHint: "Linux",
    difficulty: "Fácil",
    keyword: ["Linux"],
    statement: "No Linux, o comando chmod 755 arquivo.txt define quais permissões?",
    choices: [
      { description: "rwxr-xr-x", correct: true, rationale: "" },
      { description: "rw-rw-rw-", correct: false, rationale: "" },
      { description: "rwxrwxrwx", correct: false, rationale: "" },
      { description: "r--r--r--", correct: false, rationale: "" },
    ],
    status: "pending",
    errors: [],
  }),
  recompute({
    id: "q-4",
    testingAgencyId: "",
    bancaHint: "CESPE",
    bancaAnoHint: "",
    subjectId: "",
    materiaHint: "",
    difficulty: "Difícil",
    keyword: [],
    statement: "",
    choices: [
      { description: "Opção A", correct: false, rationale: "" },
      { description: "Opção B", correct: false, rationale: "" },
      { description: "", correct: false, rationale: "" },
      { description: "Opção D", correct: false, rationale: "" },
    ],
    status: "pending",
    errors: [],
  }),
];

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: ImportQuestion["status"] }) => {
  if (status === "ok")
    return (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 uppercase">
        OK
      </span>
    );
  if (status === "error")
    return (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600 uppercase">
        Erro
      </span>
    );
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 uppercase">
      Pendente
    </span>
  );
};

// ─── Stepper ──────────────────────────────────────────────────────────────────

const Stepper = ({ step }: { step: StepId }) => (
  <div className="flex items-center gap-2 mb-6">
    {(["upload", "review"] as StepId[]).map((s, i) => (
      <React.Fragment key={s}>
        {i > 0 && <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600 max-w-[40px]" />}
        <div className="flex items-center gap-2">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
              step === s
                ? "bg-lime-400 text-white"
                : s === "upload" && step === "review"
                ? "bg-green-500 text-white"
                : "bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400"
            }`}
          >
            {s === "upload" && step === "review" ? "✓" : i + 1}
          </div>
          <span
            className={`text-sm font-medium ${
              step === s ? "text-gray-900 dark:text-gray-100" : "text-gray-400 dark:text-gray-500"
            }`}
          >
            {s === "upload" ? "Upload" : "Revisão"}
          </span>
        </div>
      </React.Fragment>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export default function BulkImportQuestoes() {
  usePageTitle("Importação em Lote");

  const { bancas, fetchBancas } = useBancas();
  const { materias, fetchMaterias } = useMaterias();

  useEffect(() => {
    fetchBancas();
    fetchMaterias();
  }, [fetchBancas, fetchMaterias]);

  useEffect(() => {
    QuestaoService.buscarTagsDasPalavrasChaves()
      .then((resultado) => setTagsKeywords(resultado ?? []))
      .catch(() => {});
  }, []);

  const [step, setStep] = useState<StepId>("upload");
  const [questions, setQuestions] = useState<ImportQuestion[]>([]);
  const [sharedTexts, setSharedTexts] = useState<ImportSharedText[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [filter, setFilter] = useState<"all" | "ok" | "error">("all");
  const [search, setSearch] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tagError, setTagError] = useState<string | null>(null);
  const [tagsKeywords, setTagsKeywords] = useState<TagDto.TagDto[]>([]);
  const tagBoxRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [textPreviewOpen, setTextPreviewOpen] = useState(false);
  // Corpo dos textos vinculados por TEXTO_BASE_ID: eles já existem no banco e
  // não vêm no arquivo, então são buscados sob demanda e memorizados por id.
  const [textosDoBanco, setTextosDoBanco] = useState<Record<string, SharedText>>({});
  const [carregandoTexto, setCarregandoTexto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Prova de origem do lote — cabeçalho do passo de revisão. Buscada na
  // montagem (não no primeiro filtro, não há filtro aqui) para que o combobox
  // já esteja populado quando o admin chega à revisão; falha não pode
  // derrubar a tela de upload/revisão/envio.
  const [provas, setProvas] = useState<Acertei.definitions["QuestionSource"][]>([]);
  const [provasLoading, setProvasLoading] = useState(true);
  const [provasError, setProvasError] = useState(false);
  const [selectedProvaId, setSelectedProvaId] = useState<string | undefined>();
  const [novaProvaMode, setNovaProvaMode] = useState(false);
  const [novaProvaDescricao, setNovaProvaDescricao] = useState("");
  const [novaProvaArquivo, setNovaProvaArquivo] = useState<File | null>(null);

  useEffect(() => {
    QuestionSourceService.list()
      .then((list) => setProvas(list ?? []))
      .catch(() => setProvasError(true))
      .finally(() => setProvasLoading(false));
  }, []);

  const provaSelecionada = selectedProvaId
    ? provas.find((p) => String(p.id ?? "") === selectedProvaId)
    : undefined;

  // A gravação em si (criar a prova, subir o PDF, passar sourceId às
  // questões) é a próxima task — aqui só existe o portão que impede o envio
  // com uma descrição em branco.
  const novaProvaSemDescricao = novaProvaMode && !novaProvaDescricao.trim();

  const provaResumoTexto = novaProvaMode
    ? novaProvaDescricao.trim()
      ? `Nova prova a criar: "${novaProvaDescricao.trim()}"`
      : "Nenhuma prova será gravada — informe a descrição da prova nova."
    : provaSelecionada
    ? `Prova a gravar no lote: ${provaSelecionada.description ?? "—"}`
    : "Nenhuma prova será gravada neste lote.";

  const handleProvaSelectChange = (value: string) => {
    if (value === NOVA_PROVA) {
      setNovaProvaMode(true);
      setSelectedProvaId(undefined);
      return;
    }
    setNovaProvaMode(false);
    setNovaProvaDescricao("");
    setNovaProvaArquivo(null);
    setSelectedProvaId(value || undefined);
  };

  const total = questions.length;
  const validCount = questions.filter((q) => q.status === "ok").length;
  const errorCount = questions.filter((q) => q.status === "error").length;
  const current = questions[currentIdx] ?? null;

  /**
   * Resolve as dicas do arquivo assim que houver listas para casar.
   *
   * É efeito, e não parte de `parseTxt`/`loadQuestions`, por duas razões:
   * `bancas` e `materias` vêm de duas chamadas à API, e quem solta o .txt antes
   * de elas responderem resolveria zero questões; e `handleFile` é um
   * `useCallback([])`, então qualquer lista lida lá dentro seria a da primeira
   * renderização. `questions` está nas dependências de propósito: `aplicarDicas`
   * devolve o array recebido quando não há nada a preencher, então o
   * `setQuestions` aborta o re-render e a edição de qualquer campo não custa
   * nada aqui.
   */
  useEffect(() => {
    setQuestions((prev) => (prev.length ? aplicarDicas(prev, bancas, materias) : prev));
  }, [questions, bancas, materias]);

  // Quanto falta de banca/matéria no lote inteiro. Antes isto era invisível: o
  // badge dizia OK e só o envio revelava o problema, um 400 por questão.
  const semBanca = questions.filter((q) => !q.testingAgencyId).length;
  const semMateria = questions.filter((q) => !q.subjectId).length;

  // Valor que o <select> de lote mostra: o comum a todas, ou "" para misto.
  const valorComum = (ler: (q: ImportQuestion) => string) => {
    if (!questions.length) return "";
    const primeiro = ler(questions[0]);
    return questions.every((q) => ler(q) === primeiro) ? primeiro : "";
  };
  const bancaDoLote = valorComum((q) => q.testingAgencyId);
  const materiaDoLote = valorComum((q) => q.subjectId);
  const rotuloVazio = (faltando: number) =>
    faltando === questions.length ? "— selecione —" : "— varia por questão —";

  const aplicarATodas = (patch: Partial<ImportQuestion>) =>
    setQuestions((prev) => prev.map((q) => recompute({ ...q, ...patch })));

  /**
   * As dicas do arquivo que não acharam par no cadastro, agrupadas por nome.
   *
   * Sem isto o revisor lê "16 questões sem matéria" e não tem como descobrir
   * que o que falta é cadastrar "Artes" e "Literatura": o nome que veio do
   * arquivo só aparece dentro do editor, uma questão por vez. Com a lista, ou
   * ele cadastra a matéria em /materia, ou sabe exatamente o que corrigir no
   * .txt.
   */
  const agruparDicasSemPar = (
    dica: (q: ImportQuestion) => string,
    resolvida: (q: ImportQuestion) => boolean,
  ): [string, number][] => {
    const contagem = new Map<string, number>();
    questions.forEach((q) => {
      if (resolvida(q)) return;
      const nome = dica(q).trim();
      if (!nome) return;
      contagem.set(nome, (contagem.get(nome) ?? 0) + 1);
    });
    return Array.from(contagem.entries()).sort((a, b) => b[1] - a[1]);
  };
  const bancasSemPar = agruparDicasSemPar(
    (q) => q.bancaHint,
    (q) => Boolean(q.testingAgencyId),
  );
  const materiasSemPar = agruparDicasSemPar(
    (q) => q.materiaHint,
    (q) => Boolean(q.subjectId),
  );
  const listarDicas = (dicas: [string, number][]) =>
    dicas.map(([nome, n]) => `${nome} (${n})`).join(" · ");

  // Texto declarado no próprio arquivo — o único que pode ser editado aqui.
  const textoImportado = current?.sharedTextTempId
    ? sharedTexts.find((t) => t.tempId === current.sharedTextTempId) ?? null
    : null;

  // Texto base da questão aberta, venha ele do arquivo ou do banco.
  const textoDaQuestao: { title: string; source?: string; body: string } | null = (() => {
    if (!current) return null;
    if (current.sharedTextTempId) {
      const t = sharedTexts.find((x) => x.tempId === current.sharedTextTempId);
      return t ? { title: t.title, source: t.source, body: t.body } : null;
    }
    if (current.sharedTextRefId) {
      const t = textosDoBanco[current.sharedTextRefId];
      // Ainda não carregado: o botão aparece e busca ao ser clicado.
      return t
        ? { title: t.title, source: t.source, body: t.body }
        : { title: "Texto base cadastrado", body: "" };
    }
    return null;
  })();

  // Trocar de questão fecha o leitor: mantê-lo aberto trocaria o texto sob os
  // olhos do revisor, e esvaziaria o modal numa questão avulsa.
  useEffect(() => {
    setTextPreviewOpen(false);
  }, [currentIdx]);

  const abrirTexto = async () => {
    setTextPreviewOpen(true);
    const refId = current?.sharedTextRefId;
    if (!refId || textosDoBanco[refId]) return;
    try {
      setCarregandoTexto(true);
      const texto = await SharedTextService.get(refId);
      setTextosDoBanco((prev) => ({ ...prev, [refId]: texto }));
    } catch {
      notification.error({ message: "Não foi possível carregar o texto base." });
    } finally {
      setCarregandoTexto(false);
    }
  };

  // ── File handling ────────────────────────────────────────────────

  const loadQuestions = (qs: ImportQuestion[], texts: ImportSharedText[] = []) => {
    setQuestions(qs);
    setSharedTexts(texts);
    setCurrentIdx(0);
    setSearch("");
    setFilter("all");
    setTextPreviewOpen(false);
    setStep("review");
  };

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith(".txt")) {
      notification.error({ message: "Apenas arquivos .txt são suportados." });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseTxt(e.target?.result as string);
      if (parsed.questions.length) loadQuestions(parsed.questions, parsed.texts);
      else loadQuestions(DEMO);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  // ── Question mutation ────────────────────────────────────────────

  const updateAt = (idx: number, patch: Partial<ImportQuestion>) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === idx ? recompute({ ...q, ...patch }) : q))
    );
  };

  const updateCurrent = (patch: Partial<ImportQuestion>) =>
    updateAt(currentIdx, patch);

  const markCorrect = (choiceIdx: number) => {
    if (!current) return;
    updateCurrent({
      choices: current.choices.map((c, i) => ({ ...c, correct: i === choiceIdx })),
    });
  };

  const updateChoice = (
    choiceIdx: number,
    field: keyof ImportChoice,
    value: string | boolean
  ) => {
    if (!current) return;
    updateCurrent({
      choices: current.choices.map((c, i) =>
        i === choiceIdx ? { ...c, [field]: value } : c
      ),
    });
  };

  const addTag = (value?: string) => {
    const t = (value ?? tagInput).trim();
    if (!t || !current) return;
    if (t.length > TAG_MAX_LENGTH) {
      setTagError(`A tag não pode ter mais de ${TAG_MAX_LENGTH} caracteres.`);
      return;
    }
    setTagError(null);
    updateCurrent({ keyword: [...current.keyword, t] });
    setTagInput("");
  };

  const removeTag = (ti: number) => {
    if (!current) return;
    updateCurrent({ keyword: current.keyword.filter((_, i) => i !== ti) });
  };

  const tagSuggestions = (() => {
    const prefix = tagInput.trim().toLowerCase();
    if (!prefix || !current) return [];
    return tagsKeywords
      .filter((tg) => tg.description?.toLowerCase().startsWith(prefix))
      .filter((tg) => !current.keyword.includes(tg.description!))
      .slice(0, 8);
  })();

  // ── Sidebar list ─────────────────────────────────────────────────

  const filteredList = questions.filter((q) => {
    if (filter === "ok" && q.status !== "ok") return false;
    if (filter === "error" && q.status !== "error") return false;
    const qLower = (q.statement + q.bancaHint + q.materiaHint).toLowerCase();
    if (search && !qLower.includes(search.toLowerCase())) return false;
    return true;
  });

  const getBancaLabel = (q: ImportQuestion) => {
    if (q.testingAgencyId) {
      const b = bancas.find((b: any) => b.id === q.testingAgencyId);
      return b ? (b.name ?? "") : q.bancaHint;
    }
    return q.bancaHint || "—";
  };

  // ── Save ─────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    // Portão da prova nova: sem descrição não há o que criar, e meia
    // importação sem a origem que o admin acabou de declarar seria pior do
    // que perguntar de novo. Nenhuma questão é enviada enquanto isto falhar.
    if (novaProvaSemDescricao) {
      notification.error({
        message: "Informe a descrição da prova para continuar.",
      });
      return;
    }

    setSaving(true);
    setConfirmOpen(false);

    // A prova resolve antes de qualquer escrita de conteúdo: ela vale para
    // TODAS as questões do lote, então uma falha aqui não pode deixar meio
    // lote entrar sem origem — mais forte que a regra dos textos base, que só
    // pula as questões do texto que falhou. Upload primeiro, criação depois:
    // um PDF órfão em /files é lixo inofensivo e visível em /admin/files;
    // abortar depois de criar a prova (ou pior, depois dos textos base) não
    // seria.
    let sourceId: string | undefined = selectedProvaId;

    if (novaProvaMode) {
      let fileKey: string | undefined;
      if (novaProvaArquivo) {
        try {
          const uploaded = await FileService.upload(novaProvaArquivo, "question_source");
          fileKey = uploaded.file_key;
        } catch {
          setSaving(false);
          notification.error({ message: "Não foi possível enviar o PDF da prova." });
          return;
        }
      }

      try {
        const criada = await QuestionSourceService.create({
          description: novaProvaDescricao.trim(),
          ...(fileKey ? { fileKey } : {}),
        });
        sourceId = String(criada.id ?? "");
        // Disponível no combobox desta mesma visita, sem um novo GET.
        setProvas((prev) => [...prev, criada]);
      } catch {
        setSaving(false);
        notification.error({ message: "Não foi possível criar a prova." });
        return;
      }
    }

    // Índice 1-based preservado: é o número que o editor mostra ("Questão 7 de
    // 65") e o único jeito de o relatório de falhas apontar a questão certa
    // depois do filtro por status.
    const validas = questions
      .map((q, i) => ({ q, numero: i + 1 }))
      .filter(({ q }) => q.status === "ok");

    let inserted = 0;

    /**
     * Motivo por questão, e não só um contador.
     *
     * O `catch {}` vazio que existia aqui engolia o 400 do bind do Go: como
     * `validateQ` também não exigia banca nem matéria, todo lote grande perdia
     * questões sem dizer quais nem por quê. A validação agora impede o caso
     * comum; este relatório cobre o resto (sourceId inválido, rede, 500).
     */
    const falhas: { numero: number; motivo: string }[] = [];
    const motivoDe = (err: unknown) => {
      const { status, raw } = readApiError(err);
      return raw || (status ? `HTTP ${status}` : "falha de rede");
    };

    // Os textos base entram primeiro: as questões precisam do id real. Se um
    // texto falhar, suas questões são puladas em vez de entrarem sem vínculo —
    // meia importação seria pior que nenhuma.
    const idsPorTempId = new Map<string, string>();
    const textosQueFalharam = new Map<string, string>();
    const tempIdsUsados = new Set(
      validas.map(({ q }) => q.sharedTextTempId).filter(Boolean) as string[]
    );

    for (const texto of sharedTexts) {
      if (!tempIdsUsados.has(texto.tempId)) continue;
      try {
        const criado = await SharedTextService.create({
          title: texto.title,
          body: texto.body,
          source: texto.source || undefined,
        });
        idsPorTempId.set(texto.tempId, criado.id);
      } catch (err) {
        textosQueFalharam.set(texto.tempId, motivoDe(err));
      }
    }

    for (const { q, numero } of validas) {
      const motivoDoTexto = q.sharedTextTempId
        ? textosQueFalharam.get(q.sharedTextTempId)
        : undefined;
      if (motivoDoTexto) {
        falhas.push({
          numero,
          motivo: `texto base não foi criado — ${motivoDoTexto}`,
        });
        continue;
      }
      const sharedTextId =
        q.sharedTextRefId ??
        (q.sharedTextTempId ? idsPorTempId.get(q.sharedTextTempId) : undefined);

      try {
        await QuestaoService.inserirNovaQuestao({
          testingAgencyId: q.testingAgencyId,
          subjectId: q.subjectId,
          difficulty: q.difficulty as any,
          keyword: q.keyword.join(", "),
          statement: q.statement,
          // Alternativas em branco (a E não preenchida, por exemplo) não são salvas.
          choices: q.choices.filter((c) => c.description.trim()),
          ...(sharedTextId
            ? { sharedTextId, sharedTextOrder: q.sharedTextOrder }
            : {}),
          ...(sourceId ? { sourceId } : {}),
        });
        inserted++;
      } catch (err) {
        falhas.push({ numero, motivo: motivoDe(err) });
      }
    }

    setSaving(false);

    const textosCriados = idsPorTempId.size;
    const failed = falhas.length;
    const naoEnviadas = questions.length - validas.length;

    // Toda falha é mostrada com o número da questão e a mensagem da API, e o
    // aviso não expira sozinho — uma lista de 40 itens não se lê em 4,5 s.
    const MAX_LISTADAS = 8;
    notification[inserted === 0 ? "error" : failed > 0 ? "warning" : "success"]({
      message:
        inserted > 0
          ? `${inserted} questão${inserted !== 1 ? "ões" : ""} importada${inserted !== 1 ? "s" : ""}.`
          : "Nenhuma questão foi salva.",
      duration: failed > 0 ? 0 : 4.5,
      description:
        failed > 0 || textosCriados > 0 || naoEnviadas > 0 ? (
          <div className="space-y-1.5 text-xs">
            {textosCriados > 0 && (
              <p>
                {textosCriados} texto{textosCriados !== 1 ? "s" : ""} base criado
                {textosCriados !== 1 ? "s" : ""}.
              </p>
            )}
            {naoEnviadas > 0 && (
              <p>
                {naoEnviadas} questão{naoEnviadas !== 1 ? "ões" : ""} com erro de
                validação não {naoEnviadas !== 1 ? "foram" : "foi"} enviada
                {naoEnviadas !== 1 ? "s" : ""}.
              </p>
            )}
            {failed > 0 && (
              <>
                <p className="font-semibold">
                  {failed} recusada{failed !== 1 ? "s" : ""} pela API:
                </p>
                <ul className="list-disc pl-4">
                  {falhas.slice(0, MAX_LISTADAS).map((f) => (
                    <li key={f.numero}>
                      Questão {f.numero} — {f.motivo}
                    </li>
                  ))}
                </ul>
                {failed > MAX_LISTADAS && (
                  <p>…e outras {failed - MAX_LISTADAS}, listadas na revisão.</p>
                )}
              </>
            )}
          </div>
        ) : undefined,
    });

    // Lote inteiro no banco: só aqui a revisão é descartada.
    if (failed === 0 && naoEnviadas === 0) {
      setStep("upload");
      setQuestions([]);
      setSharedTexts([]);
      setCurrentIdx(0);
      setSelectedProvaId(undefined);
      setNovaProvaMode(false);
      setNovaProvaDescricao("");
      setNovaProvaArquivo(null);
      return;
    }

    /**
     * Sobrou alguma: a revisão continua aberta, com SÓ o que falta.
     *
     * Antes a tela voltava ao upload e limpava o lote em qualquer caso — quem
     * perdia 40 de 65 não tinha como saber quais, e reimportar o arquivo
     * inteiro duplicaria as 25 que entraram.
     */
    const motivoPorNumero = new Map(falhas.map((f) => [f.numero, f.motivo]));
    const remanescentes = questions
      .map((q, i) => ({ q, numero: i + 1 }))
      .filter(({ q, numero }) => motivoPorNumero.has(numero) || q.status !== "ok")
      .map(({ q, numero }) => {
        // Texto base já criado vira referência: a nova tentativa não pode
        // cadastrá-lo outra vez.
        const idReal = q.sharedTextTempId
          ? idsPorTempId.get(q.sharedTextTempId)
          : undefined;
        return recompute({
          ...q,
          serverError: motivoPorNumero.get(numero),
          ...(idReal
            ? { sharedTextTempId: undefined, sharedTextRefId: idReal }
            : {}),
        });
      });

    setQuestions(remanescentes);
    setSharedTexts((prev) =>
      prev.filter((t) => remanescentes.some((q) => q.sharedTextTempId === t.tempId))
    );
    setCurrentIdx(0);
    setFilter("all");

    // A prova do lote já existe; a nova tentativa reaproveita em vez de criar
    // uma segunda com a mesma descrição.
    if (sourceId) {
      setSelectedProvaId(sourceId);
      setNovaProvaMode(false);
      setNovaProvaDescricao("");
      setNovaProvaArquivo(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // STEP 1 — UPLOAD
  // ═══════════════════════════════════════════════════════════════════

  const renderUpload = () => (
    <div className="flex gap-6">
      {/* Main drop zone */}
      <div className="flex-1">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-14 flex flex-col items-center justify-center cursor-pointer transition-all mb-4
            ${
              isDragging
                ? "border-lime-400 bg-lime-50"
                : "border-gray-300 dark:border-slate-600 bg-white dark:bg-gray-900 hover:border-lime-400 hover:bg-lime-50"
            }`}
        >
          <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-5">
            <Upload size={26} className="text-gray-400 dark:text-gray-400" />
          </div>
          <p className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-1">
            Arraste o arquivo .txt aqui
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-300 mb-6">
            ou clique para selecionar no computador
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              fileRef.current?.click();
            }}
            className="px-7 py-2.5 bg-lime-400 hover:bg-lime-500 text-white font-semibold rounded-xl text-sm transition-colors shadow-sm"
          >
            Selecionar Arquivo
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".txt"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </div>

        <button
          onClick={() => loadQuestions(DEMO)}
          className="text-sm text-lime-600 hover:text-lime-700 font-medium"
        >
          Carregar dados de demonstração →
        </button>
      </div>

      {/* Right panel — formatting guide */}
      <div className="w-72 shrink-0 space-y-4">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <BookOpen size={14} className="text-gray-600 dark:text-gray-300" />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Formato do Arquivo
            </span>
          </div>
          <div className="p-4">
            <p className="text-[10px] text-gray-400 dark:text-gray-400 font-semibold tracking-widest mb-2">
              EXEMPLO
            </p>
            <div className="bg-gray-900 rounded-lg p-3 font-mono text-[11px] leading-relaxed mb-3">
              <p className="text-green-400">ENUNCIADO: Texto da questão</p>
              <p className="text-gray-400">BANCA: CESPE</p>
              <p className="text-gray-400">MATERIA: Informática</p>
              <p className="text-gray-400">A: Primeira alternativa</p>
              <p className="text-gray-400">B: Segunda alternativa</p>
              <p className="text-gray-400">C: Terceira alternativa</p>
              <p className="text-gray-400">D: Quarta alternativa</p>
              <p className="text-gray-400">E: Quinta alternativa (opcional)</p>
              <p className="text-white font-semibold">GABARITO: A</p>
              <p className="text-gray-400">DIFICULDADE: médio</p>
              <p className="text-gray-400">TAGS: tag1, tag2</p>
              <p className="text-gray-400">COMENTARIO: Explicação</p>
            </div>
            <p className="text-[10px] text-gray-400 dark:text-gray-400 font-semibold tracking-widest mb-2">
              TEXTO BASE COMPARTILHADO (OPCIONAL)
            </p>
            <div className="bg-gray-900 rounded-lg p-3 font-mono text-[11px] leading-relaxed mb-3">
              <p className="text-gray-400">TEXTO_BASE_TITULO: Texto I</p>
              <p className="text-gray-400">TEXTO_BASE_FONTE: CESPE 2023</p>
              <p className="text-lime-400">TEXTO_BASE:</p>
              <p className="text-gray-300">A: Bom dia, em que posso ajudar?</p>
              <p className="text-gray-300">B: Preciso do edital.</p>
              <p className="text-lime-400">FIM_TEXTO_BASE</p>
              <p className="text-gray-500">---</p>
              <p className="text-green-400">ENUNCIADO: Com base no texto, ...</p>
            </div>
            <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5 mb-3">
              <li>• Separe questões com <strong>---</strong></li>
              <li>• <strong>GABARITO:</strong> aceita A, B, C, D ou E</li>
              <li>• <strong>E:</strong> é opcional — omita a linha para uma questão com 4 alternativas</li>
              <li>• <strong>DIFICULDADE:</strong> fácil, médio ou difícil</li>
              <li>• Banca e matéria são confirmadas na revisão</li>
              <li>• Linhas com <strong>#</strong> são comentários e serão ignoradas</li>
              <li>
                • <strong>TEXTO_BASE:</strong> vale para todos os blocos seguintes até um{" "}
                <strong>FIM_TEXTO_BASE</strong> sozinho. O corpo é lido literalmente até a
                linha <strong>FIM_TEXTO_BASE</strong> — é o que permite diálogos com linhas
                como <strong>A:</strong> sem virar alternativa
              </li>
              <li>
                • <strong>TEXTO_BASE_ID:</strong> vincula a um texto já cadastrado;{" "}
                <strong>ORDEM:</strong> força a posição dentro do grupo
              </li>
            </ul>
            <a
              href="/questoes_template.txt"
              download
              className="text-xs text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              Baixar template .txt ↓
            </a>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Parser Pronto</p>
              <p className="text-xs text-gray-400 dark:text-gray-400">
                UTF-8 · separador: ---
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════
  // STEP 2 — REVIEW
  // ═══════════════════════════════════════════════════════════════════

  const renderReview = () => (
    <>
      {/* Cabeçalho da revisão — controles que valem para o lote inteiro,
          não para a questão aberta no editor (design decision 6). */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-card border border-edge bg-surface-muted px-4 py-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="bulk-import-prova"
            className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted"
          >
            Prova de origem do lote
          </label>
          <select
            id="bulk-import-prova"
            aria-label="Prova de origem do lote"
            value={novaProvaMode ? NOVA_PROVA : selectedProvaId ?? ""}
            onChange={(e) => handleProvaSelectChange(e.target.value)}
            className={cx(
              "h-10 min-w-[220px] rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
              "transition-colors duration-150 hover:border-edge-strong",
              FOCUS_RING,
            )}
          >
            <option value="">Nenhuma prova</option>
            {provasLoading ? (
              <option value="__loading__" disabled>
                Carregando provas…
              </option>
            ) : provasError ? (
              <option value="__error__" disabled>
                Não foi possível carregar as provas
              </option>
            ) : provas.length === 0 ? (
              <option value="__empty__" disabled>
                Nenhuma prova cadastrada
              </option>
            ) : (
              provas.map((p) => (
                <option key={String(p.id ?? "")} value={String(p.id ?? "")}>
                  {p.description ?? ""}
                </option>
              ))
            )}
            <option value={NOVA_PROVA}>+ Nova prova…</option>
          </select>
        </div>

        {novaProvaMode && (
          <>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="bulk-import-prova-descricao"
                className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted"
              >
                Descrição da prova nova
              </label>
              <input
                id="bulk-import-prova-descricao"
                type="text"
                value={novaProvaDescricao}
                onChange={(e) => setNovaProvaDescricao(e.target.value)}
                placeholder='Ex.: "CBMGO 2024 — Oficial"'
                className={cx(
                  "h-10 min-w-[240px] rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
                  "placeholder:text-ink-muted transition-colors duration-150 hover:border-edge-strong",
                  FOCUS_RING,
                )}
              />
              {novaProvaSemDescricao && (
                <span className="text-[11px] text-warn-ink">Descrição é obrigatória.</span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="bulk-import-prova-arquivo"
                className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted"
              >
                PDF da prova (opcional)
              </label>
              <input
                id="bulk-import-prova-arquivo"
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setNovaProvaArquivo(e.target.files?.[0] ?? null)}
                className={cx(
                  "text-[12px] text-ink-secondary",
                  "file:mr-2 file:cursor-pointer file:rounded-input file:border file:border-edge",
                  "file:bg-surface file:px-2.5 file:py-1.5 file:text-[12px] file:font-semibold file:text-ink",
                )}
              />
              {novaProvaArquivo && (
                <span className="text-[11px] text-ink-muted">{novaProvaArquivo.name}</span>
              )}
            </div>
          </>
        )}

        <span className="ml-auto text-[12px] text-ink-secondary">{provaResumoTexto}</span>
      </div>

      {/* ── Banca e matéria do lote ─────────────────────────────────────────
          Duas seleções no lugar de 130. As dicas `BANCA:`/`MATERIA:` do arquivo
          já foram casadas automaticamente (ver `aplicarDicas`); isto resolve o
          que ficou ambíguo — banca com o mesmo nome em vários anos — ou o que
          não existe cadastrado com aquele nome.

          Aplica a TODAS as questões, inclusive às já preenchidas: é o que serve
          para a banca, que é uma só no lote inteiro. Num arquivo com várias
          matérias, o de matéria achata o casamento por questão — daí o rótulo
          dizer "todas" e o contador ao lado mostrar quantas ainda faltam. */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-card border border-edge bg-surface-muted px-4 py-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="bulk-import-banca-lote"
            className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted"
          >
            Banca de todas as questões
          </label>
          <select
            id="bulk-import-banca-lote"
            aria-label="Banca de todas as questões do lote"
            value={bancaDoLote}
            onChange={(e) =>
              e.target.value && aplicarATodas({ testingAgencyId: e.target.value })
            }
            className={cx(
              "h-10 min-w-[220px] rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
              "transition-colors duration-150 hover:border-edge-strong",
              FOCUS_RING,
            )}
          >
            <option value="" disabled>
              {rotuloVazio(semBanca)}
            </option>
            {bancas.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.year} - {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="bulk-import-materia-lote"
            className="text-[10px] font-semibold uppercase tracking-widest text-ink-muted"
          >
            Matéria de todas as questões
          </label>
          <select
            id="bulk-import-materia-lote"
            aria-label="Matéria de todas as questões do lote"
            value={materiaDoLote}
            onChange={(e) => e.target.value && aplicarATodas({ subjectId: e.target.value })}
            className={cx(
              "h-10 min-w-[220px] rounded-input border border-edge bg-surface-muted px-3 text-[13px] text-ink",
              "transition-colors duration-150 hover:border-edge-strong",
              FOCUS_RING,
            )}
          >
            <option value="" disabled>
              {rotuloVazio(semMateria)}
            </option>
            {materias.map((m: any) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>

        {/* A API recusa a questão sem estes dois ids (400 no bind), então isto
            é o que separa "65 válidas" de "65 importadas". */}
        <div className="ml-auto flex flex-col items-end gap-0.5 text-[12px]">
          <span className={semBanca > 0 ? "text-warn-ink" : "text-ink-secondary"}>
            {semBanca > 0
              ? `${semBanca} questão${semBanca !== 1 ? "ões" : ""} sem banca`
              : "Banca definida em todas"}
          </span>
          <span className={semMateria > 0 ? "text-warn-ink" : "text-ink-secondary"}>
            {semMateria > 0
              ? `${semMateria} questão${semMateria !== 1 ? "ões" : ""} sem matéria`
              : "Matéria definida em todas"}
          </span>
        </div>

        {/* O que o arquivo declarou e o cadastro não tem. Ver
            `agruparDicasSemPar`: é a diferença entre "16 sem matéria" e
            "cadastre Artes, Literatura, Filosofia e Sociologia". */}
        {(bancasSemPar.length > 0 || materiasSemPar.length > 0) && (
          <div className="w-full border-t border-edge pt-2.5 text-[12px] text-ink-secondary">
            {bancasSemPar.length > 0 && (
              <p>
                <strong className="text-warn-ink">Banca do arquivo sem par no cadastro:</strong>{" "}
                {listarDicas(bancasSemPar)}. Declare o ano com{" "}
                <code className="text-ink">BANCA_ANO:</code> no .txt e cadastre a banca
                daquele ano, ou escolha acima — o ano nunca é adivinhado.
              </p>
            )}
            {materiasSemPar.length > 0 && (
              <p>
                <strong className="text-warn-ink">Matéria do arquivo sem par no cadastro:</strong>{" "}
                {listarDicas(materiasSemPar)}. Cadastre em <code className="text-ink">/materia</code>{" "}
                ou use acima o nome já existente.
              </p>
            )}
          </div>
        )}
      </div>

      <div
        className="flex border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900"
        style={{ height: "calc(100vh - 260px)", minHeight: 500 }}
      >
      {/* Question list sidebar */}
      <div className="w-52 border-r border-gray-200 dark:border-slate-700 flex flex-col shrink-0">
        {/* Search */}
        <div className="p-2.5 border-b border-gray-100 dark:border-slate-700">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar questões..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-6 pr-2 py-1.5 text-[11px] border border-gray-200 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-lime-400 placeholder-gray-400 dark:text-gray-200 dark:placeholder-gray-400"
            />
            <svg
              className="absolute left-2 top-2 w-3 h-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Textos base detectados no arquivo */}
        {sharedTexts.length > 0 && (
          <div className="border-b border-gray-100 dark:border-slate-700 px-2.5 py-2">
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
              Textos base
            </p>
            <div className="flex flex-col gap-1.5">
              {sharedTexts.map((t) => {
                const vinculadas = questions.filter(
                  (q) => q.sharedTextTempId === t.tempId
                ).length;
                return (
                  <div
                    key={t.tempId}
                    className="rounded-lg border border-lime-300 bg-lime-50 px-2 py-1.5 dark:border-[#1a2e1c] dark:bg-[#0f1a10]"
                  >
                    <p className="truncate text-[11px] font-semibold text-lime-800 dark:text-[#a3e635]">
                      {t.title}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-[#6b8f6e]">
                      {vinculadas} questão{vinculadas !== 1 ? "ões" : ""} vinculada
                      {vinculadas !== 1 ? "s" : ""}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Stats chips */}
        <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100 text-[10px]">
          <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-semibold">
            {total} total
          </span>
          <span className="px-2 py-0.5 rounded-md bg-lime-100 text-lime-700 font-semibold">
            {validCount} ok
          </span>
          <span className="px-2 py-0.5 rounded-md bg-red-100 text-red-600 font-semibold">
            {errorCount} erro
          </span>
        </div>

        {/* Filter chips */}
        <div className="flex gap-1 px-2.5 py-2 border-b border-gray-100 dark:border-slate-700">
          {(["all", "ok", "error"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[10px] px-2 py-0.5 rounded-md font-semibold transition-colors ${
                filter === f
                  ? "bg-gray-900 text-white"
                  : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              {f === "all" ? "Todas" : f === "ok" ? "OK" : "Erro"}
            </button>
          ))}
        </div>

        {/* Question list */}
        <div className="flex-1 overflow-y-auto">
          {filteredList.map((q) => {
            const realIdx = questions.indexOf(q);
            return (
              <div
                key={q.id}
                onClick={() => setCurrentIdx(realIdx)}
                className={`p-2.5 cursor-pointer border-b border-gray-100 transition-colors ${
                  realIdx === currentIdx
                    ? "bg-lime-50 dark:bg-lime-900/20 border-l-2 border-l-lime-400"
                    : q.status === "error"
                    ? "border-l-2 border-l-red-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    : "hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 truncate mr-1">
                    {getBancaLabel(q) || `Q #${realIdx + 1}`}
                  </span>
                  <StatusBadge status={q.status} />
                </div>
                <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug line-clamp-2">
                  {q.statement ? (
                    q.statement.replace(/[#*_`]/g, "").substring(0, 80)
                  ) : (
                    <em className="text-gray-400">Sem enunciado</em>
                  )}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main editor area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Editor header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-gray-800 shrink-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            <FileText size={14} />
            {current
              ? `Questão ${currentIdx + 1} de ${total}`
              : "Nenhuma questão selecionada"}
          </div>
          {current && (
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                disabled={currentIdx === 0}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={13} />
                Anterior
              </button>
              <button
                onClick={() => {
                  if (currentIdx < questions.length - 1) {
                    setCurrentIdx((i) => i + 1);
                  } else {
                    setConfirmOpen(true);
                  }
                }}
                className="flex items-center gap-1 px-3 py-1.5 bg-lime-400 hover:bg-lime-500 text-white rounded-lg text-xs font-semibold transition-colors"
              >
                {currentIdx < questions.length - 1 ? "Próxima" : "Finalizar"}
                <ChevronRight size={13} />
              </button>
            </div>
          )}
        </div>

        {/* Scrollable editor */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50 dark:bg-gray-800">
          {current ? (
            <>
              {current.serverError && (
                <div className="flex items-start gap-2.5 rounded-input border border-warn/40 bg-warn/[0.10] px-4 py-3">
                  <XCircle size={15} className="mt-0.5 shrink-0 text-warn-ink" />
                  <p className="text-xs text-warn-ink">
                    <strong>Recusada no envio anterior:</strong> {current.serverError}
                  </p>
                </div>
              )}

              {current.errors.length > 0 && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <XCircle size={15} className="text-red-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-600">
                    <strong>Problemas:</strong> {current.errors.join(" · ")}
                  </p>
                </div>
              )}

              {/* ── Metadata card ── */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-gray-900">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Metadados</span>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 tracking-widest uppercase mb-1.5 block">
                        Banca
                        {current.bancaHint && (
                          <span className="ml-1 normal-case font-normal text-gray-400">
                            (arquivo: {current.bancaHint})
                          </span>
                        )}
                      </label>
                      <Select
                        showSearch
                        className="w-full"
                        placeholder="Selecione a banca"
                        value={current.testingAgencyId || undefined}
                        onChange={(v) => updateCurrent({ testingAgencyId: v })}
                        optionFilterProp="children"
                      >
                        {bancas.map((b: any) => (
                          <Option key={b.id} value={b.id}>
                            {b.year} - {b.name}
                          </Option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 tracking-widest uppercase mb-1.5 block">
                        Matéria
                        {current.materiaHint && (
                          <span className="ml-1 normal-case font-normal text-gray-400">
                            (arquivo: {current.materiaHint})
                          </span>
                        )}
                      </label>
                      <Select
                        showSearch
                        className="w-full"
                        placeholder="Selecione a matéria"
                        value={current.subjectId || undefined}
                        onChange={(v) => updateCurrent({ subjectId: v })}
                        optionFilterProp="children"
                      >
                        {materias.map((m: any) => (
                          <Option key={m.id} value={m.id}>
                            {m.name}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 tracking-widest uppercase mb-1.5 block">
                        Dificuldade
                      </label>
                      <div className="flex gap-1.5">
                        {(["Fácil", "Média", "Difícil"] as Dificuldade[]).map((d) => (
                          <button
                            key={d}
                            onClick={() => updateCurrent({ difficulty: d })}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border-2 transition-all ${
                              current.difficulty === d
                                ? "bg-lime-400 border-lime-400 text-white"
                                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-lime-300"
                            }`}
                          >
                            {DIFF_LABELS[d]}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="relative">
                      <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 tracking-widest uppercase mb-1.5 block">
                        Tags
                      </label>
                      <div
                        ref={tagBoxRef}
                        className="flex flex-wrap gap-1.5 px-2.5 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg min-h-[36px] items-center dark:bg-gray-800"
                      >
                        {current.keyword.map((tag, ti) => (
                          <span
                            key={ti}
                            className="flex items-center gap-1 text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md px-2 py-0.5 font-medium"
                          >
                            {tag}
                            <button
                              onClick={() => removeTag(ti)}
                              className="text-gray-400 hover:text-gray-600 ml-0.5"
                            >
                              <X size={9} />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => {
                            setTagInput(e.target.value);
                            setTagError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === ",") {
                              e.preventDefault();
                              addTag();
                            }
                          }}
                          placeholder="Adicionar tag..."
                          className="text-xs outline-none flex-1 min-w-[60px] placeholder-gray-400 bg-transparent text-gray-900 dark:text-gray-100"
                        />
                      </div>
                      {tagError && (
                        <p className="text-[11px] text-red-600 mt-1">{tagError}</p>
                      )}
                      {tagSuggestions.length > 0 &&
                        tagBoxRef.current &&
                        createPortal(
                          (() => {
                            const rect = tagBoxRef.current!.getBoundingClientRect();
                            return (
                              <div
                                style={{
                                  position: "fixed",
                                  top: rect.bottom + 4,
                                  left: rect.left,
                                  width: rect.width,
                                }}
                                className="z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto"
                              >
                                {tagSuggestions.map((tg) => (
                                  <button
                                    key={tg.description}
                                    type="button"
                                    onClick={() => addTag(tg.description)}
                                    className="block w-full text-left text-xs px-2.5 py-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    {tg.description}
                                  </button>
                                ))}
                              </div>
                            );
                          })(),
                          document.body
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Enunciado card ── */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-gray-900">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Enunciado *</span>

                  {/* O enunciado de uma questão de texto base ("Com base no
                      Texto I...") não se sustenta sozinho: sem poder ler a
                      passagem, o revisor não tem como conferir o gabarito. */}
                  {textoDaQuestao && (
                    <button
                      type="button"
                      onClick={abrirTexto}
                      title={
                        textoImportado
                          ? "Ver e editar o texto base desta questão"
                          : "Ver o texto base desta questão"
                      }
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-lime-900 bg-lime-300 hover:bg-lime-400 ring-1 ring-lime-500 shadow-sm transition-colors cursor-pointer dark:bg-[#1a2e1c] dark:text-[#a3e635] dark:ring-[#a3e635] dark:hover:bg-[#243d26]"
                    >
                      <BookOpen size={14} />
                      <span className="max-w-[220px] truncate">{textoDaQuestao.title}</span>
                      {current.sharedTextOrder != null && (
                        <span className="opacity-70">· {current.sharedTextOrder}ª</span>
                      )}
                    </button>
                  )}
                </div>
                <div className="p-4">
                  <MarkdownEditor
                    value={current.statement}
                    onChange={(text) => updateCurrent({ statement: text })}
                  />
                </div>
              </div>

              {/* ── Alternatives card ── */}
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-gray-900">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                    <List size={14} />
                    Alternativas
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-400">Múltipla escolha · A a E (E opcional)</span>
                </div>
                <div className="p-4 space-y-3">
                  {current.choices.map((choice, idx) => {
                    const letter = String.fromCharCode(65 + idx);
                    const isEmpty =
                      !choice.description.trim() && (idx === 0 || idx === 1);

                    return (
                      <div
                        key={letter}
                        className={`border-2 rounded-xl overflow-hidden transition-all ${
                          choice.correct
                            ? "border-lime-300 bg-lime-50"
                            : isEmpty
                            ? "border-red-200"
                            : "border-gray-200 bg-white"
                        }`}
                      >
                        <div className="flex gap-3 p-3 items-start">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 border-2 mt-0.5 ${
                              choice.correct
                                ? "border-lime-400 text-lime-600 bg-white"
                                : "border-gray-300 text-gray-500 bg-white"
                            }`}
                          >
                            {letter}
                          </div>
                          <div className="flex-1">
                            <TextArea
                              value={choice.description}
                              onChange={(e) =>
                                updateChoice(idx, "description", e.target.value)
                              }
                              placeholder={`Texto da alternativa ${letter}...`}
                              autoSize={{ minRows: 2, maxRows: 5 }}
                              bordered={false}
                              className="text-sm !px-0 !bg-transparent"
                            />
                            {choice.correct && (
                              <TextArea
                                value={choice.rationale}
                                onChange={(e) =>
                                  updateChoice(idx, "rationale", e.target.value)
                                }
                                placeholder="Explicação da resposta correta (opcional)..."
                                autoSize={{ minRows: 1, maxRows: 3 }}
                                bordered={false}
                                className="text-xs !px-0 !bg-transparent text-gray-500 italic mt-1"
                              />
                            )}
                          </div>
                        </div>

                        <div
                          className={`flex items-center px-4 py-2 border-t ${
                            choice.correct
                              ? "border-lime-200 bg-lime-100/50"
                              : "border-gray-100 bg-gray-50"
                          }`}
                        >
                          <button
                            onClick={() => markCorrect(idx)}
                            className={`flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                              choice.correct
                                ? "text-lime-600"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {choice.correct ? (
                              <>
                                <CheckCircle size={13} className="text-lime-500" />
                                Alternativa Correta
                              </>
                            ) : (
                              <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-400" />
                                Marcar como Correta
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="h-2" />
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-400 text-sm">
              Selecione uma questão na lista
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-slate-700 shrink-0">
          <button
            onClick={() => {
              setStep("upload");
              setQuestions([]);
              setSharedTexts([]);
              setSelectedProvaId(undefined);
              setNovaProvaMode(false);
              setNovaProvaDescricao("");
              setNovaProvaArquivo(null);
            }}
            className="px-4 py-2 border-2 border-orange-400 text-orange-500 rounded-xl text-sm font-semibold hover:bg-orange-50 transition-colors"
          >
            Descartar e Recomeçar
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={validCount === 0}
            className="px-5 py-2 bg-lime-400 hover:bg-lime-500 text-white rounded-xl text-sm font-semibold transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Salvar {validCount} questão{validCount !== 1 ? "ões" : ""} válida{validCount !== 1 ? "s" : ""} ↗
          </button>
        </div>
      </div>
      </div>
    </>
  );

  // ─── Root render ──────────────────────────────────────────────────

  return (
    <DefaultLayout>
      <div>
        <div className="mb-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Importação em Lote</h2>
          <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">
            Importe múltiplas questões de uma vez a partir de um arquivo .txt
          </p>
        </div>

        <Stepper step={step} />

        {step === "upload" && renderUpload()}
        {step === "review" && renderReview()}
      </div>

      {/* Texto base declarado no arquivo: editável, no MESMO formulário do
          /admin/textos — mas em modo local, porque aqui o texto ainda não
          existe no banco (só é criado quando a importação roda). */}
      {textoImportado ? (
        <SharedTextEditorModal
          open={textPreviewOpen}
          persist={false}
          text={{
            id: "",
            title: textoImportado.title,
            body: textoImportado.body,
            source: textoImportado.source,
          }}
          onCancel={() => setTextPreviewOpen(false)}
          onSaved={(t) => {
            setSharedTexts((prev) =>
              prev.map((x) =>
                x.tempId === textoImportado.tempId
                  ? { ...x, title: t.title, body: t.body, source: t.source ?? "" }
                  : x
              )
            );
            setTextPreviewOpen(false);
          }}
        />
      ) : (
        /* Texto vinculado por TEXTO_BASE_ID: já existe no banco e é
           compartilhado por outras questões, então aqui é só leitura —
           editá-lo mudaria conteúdo fora do escopo desta importação. */
        <Modal
          open={textPreviewOpen}
          onCancel={() => setTextPreviewOpen(false)}
          footer={null}
          centered
          width={820}
          title={
            <div className="flex items-center gap-2 pr-6">
              <BookOpen size={16} className="shrink-0 text-lime-600" />
              <div className="min-w-0">
                <p className="truncate font-bold text-gray-900 dark:text-gray-100">
                  {textoDaQuestao?.title}
                </p>
                {textoDaQuestao?.source && (
                  <p className="truncate text-xs font-normal text-gray-500 dark:text-[#6b8f6e]">
                    {textoDaQuestao.source}
                  </p>
                )}
              </div>
            </div>
          }
        >
          <div className="max-h-[65vh] overflow-y-auto py-2">
            {carregandoTexto ? (
              <p className="text-sm text-gray-500 dark:text-[#6b8f6e]">Carregando o texto...</p>
            ) : textoDaQuestao?.body ? (
              <MarkdownEditor readOnly value={textoDaQuestao.body} />
            ) : (
              <p className="text-sm text-gray-500 dark:text-[#6b8f6e]">
                Este texto já está cadastrado no sistema e não veio no arquivo.
              </p>
            )}
          </div>
          <p className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-slate-700 dark:text-[#6b8f6e]">
            Vinculado por <strong>TEXTO_BASE_ID</strong>. Para alterar o conteúdo, edite-o em{" "}
            <strong>/admin/textos</strong> — ele é compartilhado por outras questões.
          </p>
        </Modal>
      )}

      {/* Confirm modal */}
      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        footer={null}
        title={<span className="font-bold text-gray-900 dark:text-gray-100">Confirmar Importação</span>}
        centered
        width={480}
      >
        <div className="py-2">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            Você está prestes a salvar{" "}
            <strong className="text-gray-900">{validCount}</strong> questão
            {validCount !== 1 ? "ões" : ""} válida{validCount !== 1 ? "s" : ""}.
            {errorCount > 0 && (
              <span className="text-red-500">
                {" "}
                {errorCount} questão{errorCount !== 1 ? "ões" : ""} com erro será
                {errorCount !== 1 ? "ão" : ""} ignorada{errorCount !== 1 ? "s" : ""}.
              </span>
            )}
          </p>

          <p className="text-sm text-ink-secondary mb-4">
            Prova de origem:{" "}
            <strong className="text-ink">
              {novaProvaMode
                ? novaProvaDescricao.trim() || "descrição pendente"
                : provaSelecionada?.description ?? "nenhuma"}
            </strong>
            {novaProvaMode && novaProvaDescricao.trim() && " (nova prova a ser criada)"}
          </p>

          <div className="space-y-2 mb-5 max-h-52 overflow-y-auto">
            {questions
              .filter((q) => q.status === "ok")
              .map((q) => (
                <div
                  key={q.id}
                  className="flex items-start gap-2.5 text-xs text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2"
                >
                  <CheckCircle size={12} className="text-lime-500 mt-0.5 shrink-0" />
                  <span className="line-clamp-1">
                    <span className="font-semibold text-gray-500 dark:text-gray-400">
                      {getBancaLabel(q) || "—"}
                    </span>{" "}
                    — {q.statement.replace(/[#*_`]/g, "").substring(0, 70)}
                    {q.statement.length > 70 ? "…" : ""}
                  </span>
                </div>
              ))}
          </div>

          {errorCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4">
              <p className="text-xs text-red-600 flex items-center gap-1.5">
                <XCircle size={12} className="shrink-0" />
                {errorCount} questão{errorCount !== 1 ? "ões" : ""} com erro não será
                {errorCount !== 1 ? "ão" : ""} importada{errorCount !== 1 ? "s" : ""}.
              </p>
            </div>
          )}

          {novaProvaSemDescricao && (
            <div className="flex items-start gap-2 rounded-input border border-warn/40 bg-warn/[0.10] px-3 py-2.5 mb-4">
              <p className="text-xs text-warn-ink">
                Descrição é obrigatória para criar a prova nova declarada no cabeçalho.
              </p>
            </div>
          )}

          <div className="flex gap-2.5 justify-end pt-2 border-t border-gray-100 dark:border-slate-700">
            <button
              onClick={() => setConfirmOpen(false)}
              className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={saving || novaProvaSemDescricao}
              className="px-5 py-2 bg-lime-400 hover:bg-lime-500 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Confirmar e Salvar"}
            </button>
          </div>
        </div>
      </Modal>
    </DefaultLayout>
  );
}
